import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { toSentenceCase } from '@noths/polaris-client-utils';
import type { Link, NonNullableLink } from '@noths/polaris-dev-ts-types';

import type { ProductState } from 'src/redux/product/types';
import { PdpBreadcrumbs } from '../featureFlags/featureFlags';

// Includes the surface segment (`all`) introduced in DISC-4169. Only `all` is
// supported today; other surfaces (e.g. `gifts`) may be added later.
const TAXONOMY_BASE_PATH = '/shop/all';
const MAX_TAXONOMY_LEVELS = 4;

// the last item in the breadcrumb hierarchy has a null rel and href
// and is also the product itself which we are not showing in the breadcrumbs
// we are also guarding against empty string values
const isNonNullableLink = (link: Link): link is NonNullableLink => {
  return (
    link.href !== null &&
    link.href.trim() !== '' &&
    link.rel !== null &&
    link.rel.trim() !== '' &&
    link.title !== null &&
    link.title.trim() !== ''
  );
};

const getBreadcrumbsHierarchy = (product: ProductState): NonNullableLink[] =>
  (product.navigation.find((n) => n.type === 'breadcrumb')?.options ?? []).filter(
    isNonNullableLink,
  );

export const getBreadcrumbsLinks = (product: ProductState): NonNullableLink[] => {
  const breadcrumbsHierarchy = getBreadcrumbsHierarchy(product);

  return breadcrumbsHierarchy.reduce<NonNullableLink[]>((items, { href, rel, title }) => {
    if (rel?.toLowerCase() === '/home') {
      return items;
    }
    return items.concat({
      rel,
      href,
      title: title && toSentenceCase(title),
    });
  }, []);
};

export const getBreadcrumbsLinksForProduct = (
  product: ProductState,
  featureFlags?: FeatureFlags,
): NonNullableLink[] => {
  if (featureFlags?.[PdpBreadcrumbs.NAME] === PdpBreadcrumbs.Taxonomy) {
    return getTaxonomyBreadcrumbsLinks(product);
  }
  return getBreadcrumbsLinks(product);
};

export const getTaxonomyBreadcrumbsLinks = (product: ProductState): NonNullableLink[] => {
  const links: NonNullableLink[] = [];
  const pathSegments: string[] = [];

  for (let level = 1; level <= MAX_TAXONOMY_LEVELS; level++) {
    const value = product.attributes[`productTypeLevel${level}`];

    if (typeof value !== 'string' || value.trim() === '') {
      break;
    }

    pathSegments.push(encodeURIComponent(value));
    const href = `${TAXONOMY_BASE_PATH}/${pathSegments.join('/')}`;

    links.push({
      href,
      rel: href,
      title: toSentenceCase(value),
    });
  }

  return links;
};
